// src/components/NotificationSystem.tsx
import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  Typography,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Announcement as AnnouncementIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

interface Notification {
  id: number;
  type: 'announcement' | 'provider' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

interface NotificationSystemProps {
  onNotificationClick?: (notification: Notification) => void;
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ onNotificationClick }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  // Simulated notifications - in a real app, these would come from your backend
  useEffect(() => {
    const mockNotifications: Notification[] = [
      {
        id: 1,
        type: 'announcement',
        title: 'New Community Announcement',
        message: 'A new announcement has been posted in your area.',
        read: false,
        createdAt: new Date().toISOString()
      },
      {
        id: 2,
        type: 'provider',
        title: 'New Service Provider',
        message: 'A new service provider has joined your community.',
        read: false,
        createdAt: new Date().toISOString()
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    
    handleClose();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'announcement':
        return <AnnouncementIcon />;
      case 'provider':
        return <BusinessIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 350, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Notifications</Typography>
        </Box>
        
        {notifications.length === 0 ? (
          <MenuItem>
            <ListItemText primary="No notifications" />
          </MenuItem>
        ) : (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{ 
                backgroundColor: notification.read ? 'transparent' : 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' }
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(notification.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
              {!notification.read && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
              )}
            </MenuItem>
          ))
        )}
      </Menu>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
      >
        <Alert onClose={() => setOpen(false)} severity="info">
          <AlertTitle>New Notification</AlertTitle>
          You have a new notification!
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationSystem;